import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { useAuth } from "../../../_helper/Auth/AuthProvider";

export default function EntryNotesPanel({ entryId, canAnnotate, isAdmin }) {
  const queryClient = useQueryClient();
  const { userData } = useAuth();
  const [body, setBody] = useState("");

  const notesQuery = useQuery({
    queryKey: ["glNotes", entryId],
    queryFn: () => GeneralLedgerService.getNotes(entryId).then((r) => r.data),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["glNotes", entryId] });
    queryClient.invalidateQueries({ queryKey: ["glEntries"] });
  };

  const addMutation = useMutation({
    mutationFn: () => GeneralLedgerService.addNote(entryId, body.trim()),
    onSuccess: () => {
      setBody("");
      refresh();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not save the note.")),
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId) => GeneralLedgerService.deleteNote(noteId),
    onSuccess: refresh,
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not delete the note.")),
  });

  const notes = notesQuery.data || [];

  return (
    <div className="gl-panel">
      <h6 className="gl-panel-title">Notes</h6>
      {notesQuery.isLoading && <Spinner size="sm" />}
      {notesQuery.isError && (
        <p className="text-danger small">{getApiErrorMessage(notesQuery.error, "Could not load notes.")}</p>
      )}
      {!notesQuery.isLoading && !notesQuery.isError && notes.length === 0 && <p className="gl-muted">No notes yet.</p>}
      <ul className="gl-note-list">
        {notes.map((note) => (
          <li key={note.id}>
            <div className="gl-note-body">{note.body}</div>
            <div className="gl-note-meta">
              {note.author?.name || "Unknown"} · {note.created_at ? new Date(note.created_at).toLocaleString() : ""}
              {(isAdmin || note.author?.id === userData?.id) && canAnnotate && (
                <Button
                  color="link"
                  size="sm"
                  className="text-danger p-0 ms-2"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(note.id)}
                >
                  Delete
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {canAnnotate && (
        <div className="gl-note-form">
          <Input
            type="textarea"
            rows="2"
            maxLength={2000}
            placeholder="Add a note for this movement…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button
            color="primary"
            size="sm"
            className="mt-2"
            disabled={!body.trim() || addMutation.isPending}
            onClick={() => addMutation.mutate()}
          >
            {addMutation.isPending ? "Saving…" : "Add note"}
          </Button>
        </div>
      )}
    </div>
  );
}
